import { PhoneNumberUtil } from 'google-libphonenumber'
import { z } from 'zod'

import { TStoreConfig } from '../store'

// 定义 地址表单 校验规则
export const customerAddressFormSchema = (
  phoneUtil?: PhoneNumberUtil,
  storeConfig?: TStoreConfig,
) =>
  z
    .object({
      country_code: z.string().trim().min(1, { message: 'Please choose your country' }),
      firstname: z.string().trim().min(1, { message: 'Please enter your first name.' }),
      lastname: z.string().trim().min(1, { message: 'Please enter your last name.' }),
      company: z.string().trim().optional(),
      street: z.tuple([
        // street[0] 第一个地址是必填
        z.string().trim().min(1, { message: 'Please enter your street address.' }),
        // street[1] 第二个地址是非必填
        z.string().optional().default(''),
        // street[2] 第三个地址是非必填
        z.string().optional().default(''),
      ]),
      city: z.string().trim().min(1, { message: 'Please enter your city.' }),
      region: z.string().trim().min(1, { message: 'Please choose your state.' }),
      region_id: z.string().min(1, { message: 'This is require field.' }),
      postcode: z.string().trim().min(1, { message: 'Please enter your zip code.' }),
      telephone: z.string().trim().min(1, { message: 'Please enter your phone number.' }),
      default_shipping: z.boolean().optional(),
      default_billing: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      const { postcode, telephone, country_code } = data
      const zipCodeRules = storeConfig?.zip_code_regular || [
        {
          country_code: 'CA',
          regular: [
            {
              example: 'A1B 2C3',
              pattern: '^[a-zA-z]{1}[0-9]{1}[a-zA-z]{1}\\s[0-9]{1}[a-zA-z]{1}[0-9]{1}$',
            },
            {
              example: 'A1B2C3',
              pattern: '^[a-zA-z]{1}[0-9]{1}[a-zA-z]{1}[0-9]{1}[a-zA-z]{1}[0-9]{1}$',
            },
            {
              example: 'A1B',
              pattern: '^[a-zA-z]{1}[0-9]{1}[a-zA-z]{1}$',
            },
          ],
        },
        {
          country_code: 'MX',
          regular: [
            {
              example: '12345',
              pattern: '^[0-9]{5}$',
            },
          ],
        },
        {
          country_code: 'US',
          regular: [
            {
              example: '12345-6789',
              pattern: '^[0-9]{5}\\-[0-9]{4}$',
            },
            {
              example: '12345',
              pattern: '^[0-9]{5}$',
            },
          ],
        },
      ]

      // 🔍 postcode
      const rule = zipCodeRules.find((r) => r?.country_code === country_code)
      if (rule) {
        const match = rule?.regular?.some((r) => new RegExp(r?.pattern || '').test(postcode.trim()))
        if (!match) {
          const examples = rule?.regular?.map((r) => r?.example).join(', ')

          ctx.addIssue({
            path: ['postcode'],
            code: z.ZodIssueCode.custom,
            message: `Invalid postcode. Valid examples are: ${examples}`,
          })
        }
      }

      // 🔍 telephone
      try {
        const parsed = phoneUtil?.parseAndKeepRawInput(telephone, country_code)
        if (!!parsed && !phoneUtil?.isValidNumberForRegion(parsed, country_code)) {
          ctx.addIssue({
            path: ['telephone'],
            code: z.ZodIssueCode.custom,
            message: `Invalid phone number`,
          })
        }
      } catch {
        ctx.addIssue({
          path: ['telephone'],
          code: z.ZodIssueCode.custom,
          message: 'Invalid phone number format',
        })
      }
    })

// 导出 地址表单 类型
export type TCustomerAddressFormValues = z.infer<ReturnType<typeof customerAddressFormSchema>>
